import { useState, useEffect, useRef } from 'react';
import { ImageDimensions } from '../utils/imageEditConstants';
import { calculateDisplayDimensions, calculateResponsiveDefaultDimensions } from '../utils/imageEditUtils';

export const useImageManagement = (
  imageFile: File | undefined,
  maxWidth: number,
  maxHeight: number
) => {
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [imageDimensions, setImageDimensions] = useState<ImageDimensions>({
    width: maxWidth,
    height: maxHeight
  });
  const [displayDimensions, setDisplayDimensions] = useState<ImageDimensions>({
    width: maxWidth,
    height: maxHeight
  });
  const [canvasReady, setCanvasReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Referenz zur aktuellen Blob-URL, um sie korrekt zu verwalten
  const currentBlobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    console.log('useImageManagement: imageFile changed:', imageFile);
    
    // Cleanup der vorherigen Blob-URL
    if (currentBlobUrlRef.current) {
      console.log('useImageManagement: revoking previous blob URL:', currentBlobUrlRef.current);
      URL.revokeObjectURL(currentBlobUrlRef.current);
      currentBlobUrlRef.current = null;
    }
    
    if (imageFile) {
      const imageUrl = URL.createObjectURL(imageFile);
      currentBlobUrlRef.current = imageUrl;
      console.log('useImageManagement: created blob URL:', imageUrl);
      setBackgroundImage(imageUrl);
      setCanvasReady(false);
      setError(null);

      const img = new Image();
      img.onload = () => {
        console.log('useImageManagement: image loaded successfully, natural size:', 
          img.naturalWidth, 'x', img.naturalHeight);
        
        const { naturalWidth, naturalHeight } = img;

        setImageDimensions({
          width: naturalWidth,
          height: naturalHeight,
        });

        const calculatedDimensions = calculateDisplayDimensions(
          naturalWidth,
          naturalHeight,
          maxWidth,
          maxHeight
        );

        console.log('useImageManagement: calculated display dimensions:', calculatedDimensions);
        
        setDisplayDimensions(calculatedDimensions);
        
        // Einheitliche Wartezeit für alle Geräte
        setTimeout(() => setCanvasReady(true), 300);
      };

      img.onerror = (error) => {
        console.error('useImageManagement: error loading image:', error);
        console.error('useImageManagement: failed to load imageUrl:', imageUrl);
        console.error('useImageManagement: imageFile details:', {
          name: imageFile?.name,
          size: imageFile?.size,
          type: imageFile?.type,
          lastModified: imageFile?.lastModified
        });
        
        console.log('useImageManagement: retrying image load after delay...');
        setTimeout(() => {
          if (currentBlobUrlRef.current === imageUrl) {
            console.log('useImageManagement: creating new image for retry...');
            const retryImg = new Image();
            retryImg.onload = () => {
              console.log('useImageManagement: retry successful');
              img.onload?.(null as any);
            };
            retryImg.onerror = () => {
              console.error('useImageManagement: retry also failed');
              setError('Fehler beim Laden des Bildes');
            };
            retryImg.src = imageUrl;
          }
        }, 200);
      };

      // Einheitliche Verzögerung vor dem Setzen der src für alle Geräte
      setTimeout(() => {
        if (currentBlobUrlRef.current === imageUrl) {
          console.log('useImageManagement: setting image src:', imageUrl);
          img.src = imageUrl;
        }
      }, 50);

      return () => {
        if (currentBlobUrlRef.current === imageUrl) {
          console.log('useImageManagement: cleanup - revoking blob URL:', imageUrl);
          URL.revokeObjectURL(imageUrl);
          currentBlobUrlRef.current = null;
        }
      };
    } else {
      console.log('useImageManagement: no imageFile, using responsive default dimensions');
      
      const calculatedDimensions = calculateResponsiveDefaultDimensions(maxWidth, maxHeight);
      
      setImageDimensions({ width: maxWidth, height: maxHeight });
      setDisplayDimensions(calculatedDimensions);
      
      // Einheitliche Wartezeit für alle Geräte
      setTimeout(() => setCanvasReady(true), 300);
    }
  }, [imageFile, maxWidth, maxHeight]);

  // Cleanup useEffect für Blob-URL beim Unmount
  useEffect(() => {
    return () => {
      if (currentBlobUrlRef.current) {
        console.log('useImageManagement: component unmount - revoking blob URL:', currentBlobUrlRef.current);
        URL.revokeObjectURL(currentBlobUrlRef.current);
        currentBlobUrlRef.current = null;
      }
    };
  }, []);

  return {
    backgroundImage,
    imageDimensions,
    displayDimensions,
    canvasReady,
    error,
    setError
  };
};
